import React, { useState, useMemo, useEffect } from "react";
import Modal from "../Modal";
import styled from '@emotion/styled';
import { isMobile } from 'react-device-detect';
import { TabbedCard } from "../Card";
import { DynamicTabController } from "../../TabController/TabController";
import { TabPanel } from "../../TabController/TabPanel";
import SelectWithLabel from "../../Inputs/Select";
import DatePicker from "../../Inputs/DatePicker";
import { OpticiansSchedule } from "../Scheduler";
import EyeTicket from "../EyeTicket/EyeTicket";
import { TabContent } from "../../StyledComponents/Tabs/Tabs";
import { FieldContainer, Row } from "../../StyledComponents/Forms/Forms";
import { formatDateToDDMMYYYY, extractHHMM, mergeDateWithTime, combineDateAndTimeUTC, timeDifference } from "../../Helpers/dateFns";
import { processNonEyeTicketRequest } from "../../../http/optician";
import ProcessEyeTicket from "../EyeTicket/Opticians/ProcessEyeTicket";
import { fetchClinicSchedule } from "../../../http/opticiansClinics";
import { StyledModal } from "../../StyledComponents/Modals/Modals";

import { fetchLinkedAccounts, getUsersEmailAddress } from "../../../http/account";
import { Alert } from "../Alert";
import { AddPerson } from "../../../assets/icons/AddPerson";
import OpticiansModal from "../Opticians/Opticians";
import { fetchEyeTicketById } from "../../../http/eyeticket";

const ModalHeader = styled.h3`
  margin-top: 0;
  color: #333;
  text-align: center;
`;

const ModalBody = styled.div`
  margin: 20px 0;
`;

const WarningBox = styled.div`
  background-color: #fff3cd;
  border-left: 8px solid #ffeeba;
  padding: 15px;
  margin-bottom: 20px;
`;

const DeleteButton = styled.button`
  background-color: #dc3545;
  color: white;
  border: none;
  padding: 10px 20px;
  margin-right: 10px;
  border-radius: 5px;
  cursor: pointer;
  &:hover {
    background-color: #c82333;
  }
`;

const CancelButton = styled.button`
  background-color: #6c757d;
  color: white;
  border: none;
  padding: 10px 20px;
  margin-right: 10px;
  border-radius: 5px;
  cursor: pointer;
  &:hover {
    background-color: #5a6268;
  }
`;

const StyledWarningModal = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1000;
  padding: 20px;
  background: white;
  border-radius: 10px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  width: 80%;
  max-width: 500px;
`;

const ModalFooter = styled.div`
  text-align: right;
  flex-shrink: 0;
  padding: 10px 0px;
`;

const Heading = styled.h3`
  margin: 0;
  margin-top: 10px;
  padding-bottom: 5px;
`;

const Paragraph = styled.p`
  margin: 0 0 20px 0;
  font-size:15px;
`;

const Button = styled.button`
  background-color: #93C572;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  transition: background-color 0.3s ease;
  padding: 10px 20px;
  margin-right: 10px;

  &:hover {
    background-color: #698D51;
  }

  &:focus {
    outline: none;
  }

  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;

const ScheduleWrapper = styled.div`
  max-height: 400px;
  overflow-y: auto;
  width:100%;
`;

export default function OpticiansClinic({
    showHideModal,
    isModalOpen,
    colours,
    clinic
}) {
    const [clinicSchedule, setClinicSchedule] = useState([]);
    const [warningMessage, setWarningMessage] = React.useState();

    const [clinicDetails, setClinicDetails] = useState(clinic);
    const [appointmentDuration, setAppointmentDuration] = useState(0);

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [missingFields, setMissingFields] = useState({});
    const [selectedScheduleData, setSelectedScheduleData] = useState(null);

    const [isNoGlassesModalOpen, setIsNoGlassesModalOpen] = useState(false);
    const [currentEyeTicket, setCurrentEyeTicket] = useState(null);
    const [eyeTicketAction, setEyeTicketAction] = useState(null);
    const [showEyeTicket, setShowEyeTicket] = useState(false);

    function ShowHideProcessEyeTicket() {
        setShowEyeTicket(!showEyeTicket);
        loadClinicSchedule();
    }

    function handleProcessTicket(action, eyeTicket) {
        setEyeTicketAction(action);
        setCurrentEyeTicket(eyeTicket);

        if (action && action !== 'Requires Spectacles') {
            setIsNoGlassesModalOpen(true);
        } else if (action === 'Requires Spectacles') {
            setShowEyeTicket(true);
        }
    }

    useEffect(() => {
        loadClinicSchedule();
    }, [clinicDetails]);

    const loadClinicSchedule = async () => {
        if (clinicDetails?.iID) {
            try {
                const clinicSchedules = await fetchClinicSchedule(clinicDetails.iID, clinicDetails.dDateOfClinic);

                if (clinicSchedules.length > 1) {
                    let appointmentDuration = timeDifference(extractHHMM(clinicSchedules[0].ScheduledAppointmentDateTime, true), extractHHMM(clinicSchedules[1].ScheduledAppointmentDateTime, true));
                    setAppointmentDuration(appointmentDuration);
                    setClinicSchedule(clinicSchedules);
                }
                setError(null);

            } catch (e) {
                setError(e);
            }
        }
    };

    async function handleCancelEyeTicket() {
        if (!currentEyeTicket) return;
        setIsLoading(true);
        try {
            var response = await processNonEyeTicketRequest(currentEyeTicket.EyeticketID, currentEyeTicket.Barcode, currentEyeTicket.Frame, eyeTicketAction);

            if (currentEyeTicket) {
                setClinicSchedule(prevEyeTickets =>
                    prevEyeTickets.map(ticket =>
                        ticket.EyeticketID === currentEyeTicket.EyeticketID
                            ? { ...ticket, Status: eyeTicketAction }
                            : ticket
                    )
                );
            }

            setCurrentEyeTicket(null);
            setError(null);
            setWarningMessage(null);
        } catch (e) {
            setError(e);
        } finally {
            setIsLoading(false);
        }
    }

    const deleteModal = currentEyeTicket && (
        <StyledWarningModal>
            <ModalHeader>{eyeTicketAction}</ModalHeader>
            <ModalBody>
                <WarningBox>
                    <strong>Warning</strong>
                    <p>By submitting this EyeTicket as {eyeTicketAction}, you won't be able to recover it.</p>
                </WarningBox>
                {currentEyeTicket && (
                    <p>Are you sure you want to submit this ticket for <b>{currentEyeTicket.Appointment}?</b></p>
                )}
            </ModalBody>
            <ModalFooter>
                <CancelButton onClick={() => setIsNoGlassesModalOpen(false)}>No</CancelButton>
                <DeleteButton onClick={() => {
                    handleCancelEyeTicket();
                    setIsNoGlassesModalOpen(false);
                }}>Yes, {eyeTicketAction}</DeleteButton>
            </ModalFooter>
        </StyledWarningModal>
    );

    return (
        <>
            {(showEyeTicket && currentEyeTicket) &&
                <ProcessEyeTicket
                    eyeTicketId={currentEyeTicket.EyeticketID}
                    barcode={currentEyeTicket.Barcode}
                    eyewear={currentEyeTicket.Frame}
                    showHideModal={ShowHideProcessEyeTicket}
                    isModalOpen={showEyeTicket}
                    colours={colours}
                    sightTestInfo={currentEyeTicket.Appointment} />}
            <Modal open={isNoGlassesModalOpen}>
                {currentEyeTicket && deleteModal}
            </Modal>
            <Modal open={isModalOpen}>
                <StyledModal>
                    <TabPanel label="Schedule">
                        <TabContent>
                            <Heading>Clinic Schedule</Heading>
                            <Paragraph>Process EyeTickets via the appointment schedule</Paragraph>
                            <ScheduleWrapper>
                                <OpticiansSchedule
                                    events={clinicSchedule}
                                    startTime={(clinicDetails?.ltStartTime.endsWith(":00") ? clinicDetails?.ltStartTime.slice(0, -3) : clinicDetails?.ltStartTime)}
                                    endTime={(clinicDetails?.ltEndTime.endsWith(":00") ? clinicDetails?.ltEndTime.slice(0, -3) : clinicDetails?.ltEndTime)}
                                    interval={appointmentDuration}
                                    selectedSchedule={setSelectedScheduleData}
                                    buttonFn={handleProcessTicket}
                                />
                            </ScheduleWrapper>
                        </TabContent>
                    </TabPanel>
                    <ModalFooter>
                        {isLoading && <Alert type='information'>Loading..</Alert>}
                        {error && !isLoading && <Alert type='error'>Unable to process request due to <b>{error.message}</b></Alert>}
                        {warningMessage && <Alert type='warning'>{warningMessage}</Alert>}
                        <CancelButton onClick={showHideModal}>Close</CancelButton>
                    </ModalFooter>
                </StyledModal>
            </Modal>
        </>
    );
}
